import AdminProductSubscribedApi from "api/wenImmo/admin/ProductSubscribedApi";
import ProductSubscribedApi from "api/wenImmo/ProductSubscribedApi";
import { EProductDocumentType } from "common/enums/ProductSheet/DocumentsTemplates/EProductDocumentType";
import ProductSubscribedSignedFilesRequestResource from "common/resources/ProductSubscribed/File/ProductSubscribedSignedFilesRequestResource";
import { ESubStep } from "common/enums/Subscription/ESubStep";
import FileResponseResource from "common/resources/Files/FileResponseResource";
import ProductSubscribedServiceClassToken from "common/resources/injectables/ProductSubscribedServiceClassToken";
import ProductSubscribedToFileAdminRequestResource from "common/resources/ProductSubscribed/File/ProductSubscribedToFileAdminRequestResource";
import ProductSubscribedToFileResponseResource from "common/resources/ProductSubscribed/File/ProductSubscribedToFileResponseResource";
import ProductSubscribedResponseResource from "common/resources/ProductSubscribed/ProductSubscribedResponseResource";
import ProductSubscriptionUpdateResource from "common/resources/ProductSubscribed/ProductSubscriptionUpdateResource";
import ProductSubscribedContrepartieUpdateResource from "common/resources/ProductSubscribed/ProductSubscribedContrepartieUpdateResource";
import ProductSubscribedTransferDossierRequestResource from "common/resources/ProductSubscribed/ProductSubscribedTransferDossierRequestResource";
import ProductSubscribedTransferModeResponseResource from "common/resources/ProductSubscribed/ProductSubscribedTransferModeResponseResource";
import { IPagination } from "components/elements/InfiniteScroll";
import { container, singleton } from "tsyringe";
import toValidationError from "utils/toValidationError";

@singleton()
export default class ProductSubscribedService implements ProductSubscribedServiceClassToken {
	private api: ProductSubscribedApi = container.resolve(ProductSubscribedApi);
	private adminApi: AdminProductSubscribedApi = container.resolve(AdminProductSubscribedApi);

	public async exists(uid: string) {
		return !!this.api.getByUid(uid).catch(toValidationError);
	}

	public async countBySteps(steps: ESubStep[], search?: string | null) {
		return this.api.countBySteps(steps, search).catch(toValidationError);
	}

	public async countByStep(step: ESubStep) {
		return this.api.countByStep(step).catch(toValidationError);
	}

	public async countByStepsAdmin(steps: ESubStep[], search?: string | null) {
		return this.adminApi.countBySteps(steps, search).catch(toValidationError);
	}

	public async getAllByStep(steps: ESubStep[], pagination: IPagination, search?: string | null): Promise<ProductSubscribedResponseResource[]> {
		return this.api.getAllByStep(steps, pagination, search).catch(toValidationError);
	}

	public async getByUid(uid: string): Promise<ProductSubscribedResponseResource> {
		return this.api.getByUid(uid).catch(toValidationError);
	}

	public async getAllByStepAdmin(steps: ESubStep[], pagination: IPagination, search?: string | null): Promise<ProductSubscribedResponseResource[]> {
		return this.adminApi.getAllByStep(steps, pagination, search).catch(toValidationError);
	}

	public async updateStatus(uid: string, body: ProductSubscriptionUpdateResource): Promise<ProductSubscribedResponseResource> {
		return this.adminApi.updateStatus(uid, body).catch(toValidationError);
	}

	public async uploadFileAdmin(file: File): Promise<FileResponseResource> {
		return this.adminApi.uploadFile(file).catch(toValidationError);
	}

	public async attachFile(attachedFile: ProductSubscribedToFileAdminRequestResource): Promise<ProductSubscribedToFileResponseResource> {
		return this.adminApi.attachFile(attachedFile).catch(toValidationError);
	}

	public async deleteFile(fileUid: string) {
		return this.adminApi.deleteFile(fileUid).catch(toValidationError);
	}

	public async getAllValidatedByClientId(clientId: string, pagination: IPagination, search?: string | null): Promise<ProductSubscribedResponseResource[]> {
		return this.api.getAllValidatedByClientId(clientId, pagination, search).catch(toValidationError);
	}

	public async getAllValidatedByCorporationId(corporationId: string, pagination: IPagination, search?: string | null): Promise<ProductSubscribedResponseResource[]> {
		return this.api.getAllValidatedByCorporationId(corporationId, pagination, search).catch(toValidationError);
	}

	public buildFileUrlByFileId(fileId: string) {
		return this.api.buildFileUrlByFileId(fileId);
	}

	public async transferDocumentsToSg(productSubscribedId: string): Promise<void> {
		return this.api.transferDocumentsToSG(productSubscribedId).catch(toValidationError);
	}

	/** Façon de transmettre un dossier signé : transmission simple, ou signature de la société de gestion (private equity) et sa demande en cours. */
	public async getTransferMode(productSubscribedId: string): Promise<ProductSubscribedTransferModeResponseResource> {
		return this.api.getTransferMode(productSubscribedId).catch(toValidationError);
	}

	/** Private equity : transmet le dossier à la personne qui le signe pour la société de gestion. */
	public async attachHexaRectification(productSubscribedId: string, document: File, proof: File): Promise<void> {
		return this.api.attachHexaRectification(productSubscribedId, document, proof).catch(toValidationError);
	}

	public async prepareHexaSignatures(productSubscribedId: string, signatory: ProductSubscribedTransferDossierRequestResource, replacement: boolean): Promise<void> {
		return this.api.prepareHexaSignatures(productSubscribedId, signatory, replacement).catch(toValidationError);
	}

	public async transferDossier(productSubscribedId: string, signatory: ProductSubscribedTransferDossierRequestResource): Promise<void> {
		return this.api.transferDossier(productSubscribedId, signatory).catch(toValidationError);
	}

	public async getSignedDocuments(productSubscribedId: string): Promise<ProductSubscribedSignedFilesRequestResource[]> {
		return this.api.getSignedDocuments(productSubscribedId).catch(toValidationError);
	}

	public buildSignedDocumentUrl(productSubscribedId: string, signedDocument: ProductSubscribedSignedFilesRequestResource) {
		return this.api.buildSignedDocumentUrl(productSubscribedId, signedDocument);
	}

	public buildSignedDocumentsZipUrl(productSubscribedId: string) {
		return this.api.buildSignedDocumentsZipUrl(productSubscribedId);
	}

	public buildFileUrlByFileIdAdmin(fileId: string) {
		return this.adminApi.buildFileUrlByFileId(fileId);
	}

	public async getSignableDocumentsTypesAdmin(productSubscribedId: string): Promise<EProductDocumentType[]> {
		return this.adminApi.getSignableDocumentsTypes(productSubscribedId).catch(toValidationError);
	}

	public buildSignableDocumentUrlAdmin(productSubscribedId: string, docType: EProductDocumentType) {
		return this.adminApi.buildSignableDocumentUrl(productSubscribedId, docType);
	}

	public buildSignableDocumentsZipUrlAdmin(productSubscribedId: string) {
		return this.adminApi.buildSignableDocumentsZipUrl(productSubscribedId);
	}

	public buildRtoSignableDocumentUrlAdmin(productSubscribedId: string) {
		return this.adminApi.buildRtoSignableDocument(productSubscribedId);
	}

	public async updateToLastProductSheet(productSubscribedId: string) {
		return this.adminApi.updateToLastProductSheet(productSubscribedId).catch(toValidationError);
	}

	public async reportAnomaly(productSubscribedId: string, body: { message: string }) {
		return this.api.reportAnomaly(productSubscribedId, body).catch(toValidationError);
	}

	public async validateDocuments(productSubscribedId: string) {
		return this.api.validateDocuments(productSubscribedId).catch(toValidationError);
	}

	public async getSignableDocumentsTypes(productSubscribedId: string): Promise<EProductDocumentType[]> {
		return this.api.getSignableDocumentsTypes(productSubscribedId).catch(toValidationError);
	}

	public buildSignableDocumentUrl(productSubscribedId: string, docType: EProductDocumentType) {
		return this.api.buildSignableDocumentUrl(productSubscribedId, docType);
	}

	public buildSignableDocumentsZipUrl(productSubscribedId: string) {
		return this.api.buildSignableDocumentsZipUrl(productSubscribedId);
	}

	/**
	 * Dossier complet avant signature, à remettre à la société de gestion pour relecture (private equity).
	 * L'échec est rendu tel quel : un tableau quand le serveur a écrit le motif du refus, autre chose pour une panne.
	 */
	public async getCompleteDossierZip(productSubscribedId: string): Promise<Blob> {
		return this.api.getCompleteDossierZip(productSubscribedId);
	}

	public buildRtoSignableDocumentUrl(productSubscribedId: string) {
		return this.api.buildRtoSignableDocument(productSubscribedId);
	}

	public async notifyReadyForSGTransfer(productSubscribedId: string) {
		return this.adminApi.notifyReadyForSGTransfer(productSubscribedId).catch(toValidationError);
	}

	public async updateContrepartieAdmin(uid: string, body: ProductSubscribedContrepartieUpdateResource): Promise<void> {
		return this.adminApi.updateContrepartie(uid, body).catch(toValidationError);
	}
}
